import { Navigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { decodeEd25519Pubkey } from '@cef-ai/wallet-identity';
import { useWallet } from '~/wallet';
import { AppPage } from '~/ui/AppPage';
import { Card, CopyValue } from '~/ui/primitives';
import { AccountIdentity } from './AccountIdentity';

const CAPTION = { margin: 0, color: 'var(--text-muted)', font: '400 13px/1.5 var(--font-sans)' } as const;

/**
 * The `0x`-prefixed Ed25519 public key — what the platform identifies the user
 * by (signed rosters, invite fields), and NOT a chain address (CEF-AI/wallet#107).
 *
 * Derived exactly as a host app's copy is, in the same two steps on the same
 * input: `EmbedWallet.asSigner().publicKey` is `decodeEd25519Pubkey(addresses.solana)`,
 * and `@cef-ai/wallet-react`'s `buildConnectedWallet` prefixes its lowercase hex
 * with `0x` to make `ConnectedWallet.publicKeyHex`. So this is the value host
 * apps receive on login rather than a parallel derivation that could drift from
 * it. (Recovering it from the address costs nothing in exposure — the Solana
 * address IS the public key, base58-encoded — and reaches no key material.)
 *
 * `null` when the session's Solana address does not decode to a 32-byte key:
 * `addresses` arrives from the server on a bearer-resumed session, and a
 * malformed one must cost this row, not the whole account page.
 */
function publicKeyHex(solanaAddress: string): string | null {
  try {
    return '0x' + Buffer.from(decodeEd25519Pubkey(solanaAddress)).toString('hex');
  } catch {
    return null;
  }
}

export const Account = observer(() => {
  const { identity } = useWallet();

  if (!identity.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  const addr = identity.addresses!;
  const publicKey = publicKeyHex(addr.solana);

  return (
    <AppPage title="Account">
      <AccountIdentity account={identity.account} />
      {publicKey && (
        <>
          <Card>
            <CopyValue label="Public key" value={publicKey} truncate={false} />
          </Card>
          <p style={CAPTION}>
            Your platform identity — paste this where an app asks for your public key (invites, rosters, support). The
            chain addresses below are not interchangeable with it.
          </p>
        </>
      )}
      <Card>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          <CopyValue label="Cere (SS58)" value={addr.cere} />
          <CopyValue label="Solana (base58)" value={addr.solana} />
          <CopyValue label="EVM (0x)" value={addr.evm} />
        </div>
      </Card>
      <p style={CAPTION}>
        These addresses are derived from your passkey on this device. The same identity works across Cere, Solana, and
        EVM.
      </p>
    </AppPage>
  );
});
